import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import { PlayerProvider } from './context/PlayerContext';
import { Header } from './components/layout/Header';
import { BottomNav } from './components/layout/BottomNav';
import { RouteEffects } from './components/layout/RouteEffects';
import { GlobalPlayer } from './components/player/GlobalPlayer';
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import EventsPage from './pages/EventsPage';
import ArtistPage from './pages/ArtistPage';
import BookingPage from './pages/BookingPage';
import PlaylistsPage from './pages/PlaylistsPage';
import SupportPage from './pages/SupportPage';
import DashboardPage from './pages/DashboardPage';
import AnalyticsPage from './pages/AnalyticsPage';
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';
import LibraryPage from './pages/LibraryPage';
import BookingHistoryPage from './pages/BookingHistoryPage';
import WalletPage from './pages/WalletPage';
import LivePage from './pages/LivePage';
import LegalPage from './pages/LegalPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AccessRequestPage from './pages/AccessRequestPage';
import ActivateAccountPage from './pages/ActivateAccountPage';
import OnboardingPage from './pages/OnboardingPage';
import ProducerPage from './pages/ProducerPage';
import TrackPage from './pages/TrackPage';
import ArtistDashboardPage from './pages/ArtistDashboardPage';
import ArtistBookingPage from './pages/ArtistBookingPage';
import InquireOfferPage from './pages/InquireOfferPage';
import LegalAdvisorPage from './pages/LegalAdvisorPage';
import DirectorDepartmentsPage from './pages/DirectorDepartmentsPage';
import { CookieBanner } from './components/CookieBanner';
import { useAuth } from './context/AuthContext';
import './App.css';

const PRIVATE_ACCESS_CODE = "7716";

function DirectorDepartmentsRoute() {
  const { isDirector, loading, user } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-pulse-bg flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user && !isDirector) {
    return (
      <div className="min-h-screen bg-pulse-bg flex flex-col items-center justify-center p-6 text-center text-white">
        <p className="text-pulse-muted mb-4 max-w-md">This area is for the platform director only.</p>
        <a href="/dashboard" className="text-cyan-400 hover:underline">Back to dashboard</a>
      </div>
    );
  }
  if (!user) {
    return (
      <div className="min-h-screen bg-pulse-bg flex flex-col items-center justify-center p-6 text-center text-white">
        <p className="text-pulse-muted mb-4">Sign in to continue.</p>
        <a href="/login" className="text-cyan-400 hover:underline">Log in</a>
      </div>
    );
  }
  return <DirectorDepartmentsPage />;
}

function PrivateGate({ children }) {
  const [granted, setGranted] = useState(() => sessionStorage.getItem('pulse_access') === 'granted');
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);

  if (granted) return children;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code === PRIVATE_ACCESS_CODE) {
      sessionStorage.setItem('pulse_access', 'granted');
      setGranted(true);
    } else {
      setError(true);
      setTimeout(() => setError(false), 1500);
    }
  };

  return (
    <div className="min-h-screen bg-pulse-bg flex items-center justify-center px-4">
      <form onSubmit={handleSubmit} className="w-full max-w-xs text-center">
        <div className="w-16 h-16 rounded-2xl bg-pulse-primary mx-auto flex items-center justify-center mb-6">
          <span className="font-heading font-extrabold text-2xl text-black">P</span>
        </div>
        <h1 className="font-heading font-bold text-xl text-white mb-1">PulseMusic</h1>
        <p className="text-pulse-muted text-xs mb-6">Acceso privado — Beta interna</p>
        <input
          type="password"
          inputMode="numeric"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Codigo de acceso"
          autoFocus
          className={`w-full bg-pulse-surface border rounded-xl px-4 py-3 text-sm text-center tracking-widest focus:ring-2 focus:ring-cyan-500 focus:outline-none transition-colors ${error ? 'border-red-500 shake' : 'border-pulse-border'}`}
        />
        <button type="submit" className="w-full mt-4 bg-pulse-primary text-black font-bold py-3 rounded-xl hover:bg-pulse-primary-hover transition-colors">
          Entrar
        </button>
        {error && <p className="text-red-400 text-xs mt-3">Codigo incorrecto</p>}
        <p className="text-pulse-muted text-[10px] mt-6">Plataforma en desarrollo. Acceso restringido.</p>
      </form>
    </div>
  );
}

function App() {
  return (
    <PrivateGate>
    <LanguageProvider>
      <AuthProvider>
        <PlayerProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <RouteEffects />
              <div className="min-h-screen bg-pulse-bg text-white">
                <Header />
                <main>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/search" element={<SearchPage />} />
                    <Route path="/events" element={<EventsPage />} />
                    <Route path="/artist/:id" element={<ArtistPage />} />
                    <Route path="/booking/:id" element={<BookingPage />} />
                    <Route path="/playlists" element={<PlaylistsPage />} />
                    <Route path="/support" element={<SupportPage />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/director/departamentos" element={<DirectorDepartmentsRoute />} />
                    <Route path="/analytics" element={<AnalyticsPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/library" element={<LibraryPage />} />
                    <Route path="/bookings" element={<BookingHistoryPage />} />
                    <Route path="/wallet" element={<WalletPage />} />
                    <Route path="/live" element={<LivePage />} />
                    <Route path="/legal" element={<LegalPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/activate-access" element={<AccessRequestPage />} />
                    <Route path="/activate-account" element={<ActivateAccountPage />} />
                    <Route path="/onboarding" element={<OnboardingPage />} />
                    <Route path="/producer" element={<ProducerPage />} />
                    <Route path="/track/:id" element={<TrackPage />} />
                    <Route path="/artist-dashboard" element={<ArtistDashboardPage />} />
                    <Route path="/artist-booking" element={<ArtistBookingPage />} />
                    <Route path="/inquire/:offerId" element={<InquireOfferPage />} />
                    <Route path="/legal-advisor" element={<LegalAdvisorPage />} />
                  </Routes>
                </main>
                <GlobalPlayer />
                <BottomNav />
                <CookieBanner />
                <Toaster
                  position="top-right"
                  toastOptions={{
                    style: {
                      background: '#141417',
                      border: '1px solid #27272A',
                      color: '#fff',
                    }
                  }}
                />
              </div>
          </Router>
        </PlayerProvider>
      </AuthProvider>
    </LanguageProvider>
    </PrivateGate>
  );
}

export default App;
